
import {
  getItemsOperation,
  resolveItems,
  supportFacets
} from '../../operations/getItems';
import { clearItems } from '../../operations/clearItems';
import { deleteItemsOperation } from '../../operations/deleteItems';
import { IEntry, EntryType, IDirectoryPage } from '../../utilities/fileSystem/FileEntries';
import { KeyFacet, numberPart, subKeyPart, stringPart } from '../../models/KeyFacet';
import { AutoKeyFacet } from '../../models/AutoKeyFacet';
import { Facet } from '../../models/Facet';
import {
  IAddon, createAddon, registerOperationHandler, createSingleItemOperationHandler, ISingleOperationPayload, IOperationHandlerThunkAction
} from '../../addons/registerOperationHandler';
import { IItemCacheDispatch, IItemCacheThunkAction } from '../../store/actions/ItemCacheActions';
import { defaultChildItemSetFacet, childItemsFacet, parentItemFacet } from '../../facets/SetFacets';
import {
  displayNameFacet, fileSizeFacet, fileNameFacet, folderFacet, parentFolderItemFacet, dateModifiedFacet
} from '../../facets/ItemFacets';
import { readItems } from '../../operations/readItems';
import { IItemCache } from '../../models/IItemCacheObserver';
import { ItemResolver } from '../../models/items/ItemResolver';
import { resolveNewPage, resolveExistingPage } from '../../models/items/PageHelpers';
import { sourceFileFacet, entryBatchFacet } from '../../facets/FileSystemFacets';

const entryBatchKeyFacet = new AutoKeyFacet('batch');

function getNextEntryBatchKey(): string {
  return entryBatchKeyFacet.serializeNext();
}

const entryBatchChildrenKeyFacet = new KeyFacet<{
  batch: typeof entryBatchKeyFacet.type;
}>('batchChildren', {
  batch: subKeyPart(entryBatchKeyFacet)
});

const fileEntryKeyFacet = new KeyFacet<{
  batch: typeof entryBatchKeyFacet.type;
  index: number;
  path: string;
}>('fileEntry', {
  batch: subKeyPart(entryBatchKeyFacet),
  index: numberPart(),
  path: stringPart()
});

const fileEntryChildrenKeyFacet = new KeyFacet<{
  fileEntry: typeof fileEntryKeyFacet.type;
}>('fileEntryChildren', {
  fileEntry: subKeyPart(fileEntryKeyFacet)
});

const fileEntryDirectoryPageKeyFacet = new KeyFacet<{
  fileEntryChildren: typeof fileEntryChildrenKeyFacet.type;
  startIndex: number;
}>('fileEntryDirectoryPage', {
  fileEntryChildren: subKeyPart(fileEntryChildrenKeyFacet),
  startIndex: numberPart()
});

const entryFacet = new Facet<IEntry>('entry');

const rootEntriesFacet = new Facet<IEntry[]>('rootEntries');

const directoryPageFacet = new Facet<IDirectoryPage>('directoryPage');

export const fileEntryAddon: IAddon = createAddon(() => {
  return (dispatch: IItemCacheDispatch) => {
    dispatch(supportFacets({
      keyFacet: entryBatchKeyFacet,
      facets: [defaultChildItemSetFacet]
    }));
    dispatch(supportFacets({
      keyFacet: entryBatchChildrenKeyFacet,
      facets: [childItemsFacet]
    }));
    dispatch(supportFacets({
      keyFacet: fileEntryKeyFacet,
      facets: [
        displayNameFacet,
        defaultChildItemSetFacet,
        sourceFileFacet,
        fileSizeFacet,
        fileNameFacet,
        folderFacet,
        parentFolderItemFacet,
        dateModifiedFacet
      ]
    }));
    dispatch(supportFacets({
      keyFacet: fileEntryChildrenKeyFacet,
      facets: [childItemsFacet]
    }));
    dispatch(supportFacets({
      keyFacet: fileEntryDirectoryPageKeyFacet,
      facets: [childItemsFacet]
    }));

    dispatch(registerOperationHandler(entryBatchKeyFacet, getItemsOperation)(
      createSingleItemOperationHandler()(getEntryBatchItem)
    ));
    dispatch(registerOperationHandler(entryBatchChildrenKeyFacet, getItemsOperation)(
      createSingleItemOperationHandler()(getEntryBatchChildrenItem)
    ));
    dispatch(registerOperationHandler(fileEntryKeyFacet, getItemsOperation)(
      createSingleItemOperationHandler()(getFileEntryItem)
    ));
    dispatch(registerOperationHandler(fileEntryChildrenKeyFacet, getItemsOperation)(
      createSingleItemOperationHandler()(getFileEntryChildrenItem)
    ));
    dispatch(registerOperationHandler(fileEntryDirectoryPageKeyFacet, getItemsOperation)(
      createSingleItemOperationHandler()(getFileEntryDirectoryPageItem)
    ));
    dispatch(registerOperationHandler(entryBatchKeyFacet, deleteItemsOperation)(
      createSingleItemOperationHandler()(deleteEntryBatchItem)
    ));
  };
});

export function createEntryBatch(payload: {
  entries: IEntry[];
}): IItemCacheThunkAction<{
  itemKey: string;
}> {
  return (dispatch: IItemCacheDispatch) => {
    dispatch(fileEntryAddon);

    const entryBatchKey = getNextEntryBatchKey();

    dispatch(resolveItems({
      items: {
        [entryBatchKey]: {
          ...entryBatchFacet.pack({}),
          ...rootEntriesFacet.pack(payload.entries)
        }
      }
    }));

    return {
      itemKey: entryBatchKey
    };
  };
}

function getEntryBatchItem(
  payload: ISingleOperationPayload<typeof entryBatchKeyFacet.type, typeof getItemsOperation.type>
): IOperationHandlerThunkAction {
  const {
    itemKey
  } = payload;

  const batch = entryBatchKeyFacet.deserialize(itemKey);

  return async (dispatch: IItemCacheDispatch) => {
    const rootEntries = dispatch(readItems((itemCache: IItemCache) => itemCache.demandItemFacet(rootEntriesFacet, itemKey)));

    if (rootEntries) {
      dispatch(resolveItems({
        items: {
          [itemKey]: {
            ...entryBatchFacet.pack({}),
            ...defaultChildItemSetFacet.pack({
              itemKey: entryBatchChildrenKeyFacet.serialize({
                batch: batch
              })
            })
          }
        }
      }));
    }
  };
}

function getEntryBatchChildrenItem(
  payload: ISingleOperationPayload<typeof entryBatchChildrenKeyFacet.type, typeof getItemsOperation.type>
): IOperationHandlerThunkAction {
  const {
    itemKey
  } = payload;

  const batchChildren = entryBatchChildrenKeyFacet.deserialize(itemKey);

  const {
    batch
  } = batchChildren;

  const batchKey = entryBatchKeyFacet.serialize(batch);

  return async (dispatch: IItemCacheDispatch) => {
    const rootEntries = dispatch(readItems((itemCache: IItemCache) => itemCache.demandItemFacet(rootEntriesFacet, batchKey)));

    if (rootEntries) {
      const itemResolver = new ItemResolver();

      const childItemKeys: string[] = [];

      let index = 0;

      for (const entry of rootEntries) {
        const childEntryKey = fileEntryKeyFacet.serialize({
          batch: batch,
          index: index,
          path: entry.name
        });

        itemResolver.resolveItems({
          items: {
            [childEntryKey]: {
              ...entryFacet.pack(entry)
            }
          }
        });

        childItemKeys.push(childEntryKey);
        index++;
      }

      itemResolver.resolveItems({
        items: {
          [itemKey]: {
            ...childItemsFacet.pack({
              itemKeys: childItemKeys
            })
          }
        }
      });

      dispatch(itemResolver.execute());
    }
  };
}

function getFileEntryItem(
  payload: ISingleOperationPayload<typeof fileEntryKeyFacet.type, typeof getItemsOperation.type>
): IOperationHandlerThunkAction {
  const {
    itemKey
  } = payload;

  return async (dispatch: IItemCacheDispatch) => {
    const entry = dispatch(readItems((itemCache: IItemCache) => itemCache.demandItemFacet(entryFacet, itemKey)));

    if (entry) {
      dispatch(resolveItems({
        items: {
          [itemKey]: {
            ...displayNameFacet.pack(entry.name),
            ...(entry.type === EntryType.directory ?
              {
                ...folderFacet.pack({}),
                ...fileNameFacet.pack({
                  name: entry.name,
                  extension: ''
                }),
                ...defaultChildItemSetFacet.pack({
                  itemKey: fileEntryChildrenKeyFacet.serialize({
                    fileEntry: fileEntryKeyFacet.deserialize(itemKey)
                  })
                })
              } :
              {
                ...defaultChildItemSetFacet.pack(undefined),
                ...folderFacet.pack(undefined)
              }),
            ...(entry.type === EntryType.file ? {
              ...fileNameFacet.pack(getFileName(entry.name))
            } : {})
          }
        }
      }));

      if (entry.type === EntryType.file || entry.type === EntryType.unknown) {
        // TODO Move handling of EntryType.unknown into its own key.
        const file = await entry.getFile();

        dispatch(resolveItems({
          items: {
            [itemKey]: {
              ...fileNameFacet.pack(getFileName(file.name)),
              ...folderFacet.pack(undefined),
              ...fileSizeFacet.pack(file.size),
              ...dateModifiedFacet.pack(file.lastModified),
              ...sourceFileFacet.pack(file)
            }
          }
        }));
      }
    }
  };
}

function getFileEntryChildrenItem(
  payload: ISingleOperationPayload<typeof fileEntryChildrenKeyFacet.type, typeof getItemsOperation.type>
): IOperationHandlerThunkAction {
  const {
    itemKey
  } = payload;

  const fileEntryChildren = fileEntryChildrenKeyFacet.deserialize(itemKey);

  const {
    fileEntry
  } = fileEntryChildren;

  const fileEntryKey = fileEntryKeyFacet.serialize(fileEntry);

  return async (dispatch: IItemCacheDispatch) => {
    const entry = dispatch(readItems((itemCache: IItemCache) => {
      return itemCache.demandItemFacet(entryFacet, fileEntryKeyFacet.serialize(fileEntry));
    }));

    if (entry && entry.type === EntryType.directory) {
      const directoryPage = await entry.getEntries();

      const itemResolver = new ItemResolver();

      const {
        entries,
        getEntries
      } = directoryPage;

      const childItemKeys: string[] = [];

      let index = 0;

      for (const childEntry of entries) {
        const childEntryKey = fileEntryKeyFacet.serialize({
          ...fileEntry,
          index: index,
          path: `${fileEntry.path}/${childEntry.name}`
        });

        itemResolver.resolveItems({
          items: {
            [childEntryKey]: {
              ...entryFacet.pack(childEntry),
              ...parentFolderItemFacet.pack({
                itemKey: fileEntryKey
              })
            }
          }
        });

        childItemKeys.push(childEntryKey);
        index++;
      }

      if (getEntries) {
        const childDirectoryPageKey = fileEntryDirectoryPageKeyFacet.serialize({
          fileEntryChildren: fileEntryChildren,
          startIndex: index
        });

        itemResolver.merge(resolveNewPage({
          itemKey: childDirectoryPageKey
        }).itemResolver);

        itemResolver.resolveItems({
          items: {
            [childDirectoryPageKey]: {
              ...directoryPageFacet.pack(directoryPage)
            }
          }
        });

        childItemKeys.push(childDirectoryPageKey);
      }

      itemResolver.resolveItems({
        items: {
          [itemKey]: {
            ...childItemsFacet.pack({
              itemKeys: childItemKeys
            }),
            ...parentItemFacet.pack({
              itemKey: fileEntryKey
            })
          }
        }
      });

      dispatch(itemResolver.execute());
    }
  };
}

function getFileEntryDirectoryPageItem(
  payload: ISingleOperationPayload<typeof fileEntryDirectoryPageKeyFacet.type, typeof getItemsOperation.type>
): IOperationHandlerThunkAction {
  const {
    itemKey
  } = payload;

  const {
    fileEntryChildren,
    startIndex
  } = fileEntryDirectoryPageKeyFacet.deserialize(itemKey);

  const {
    fileEntry
  } = fileEntryChildren;

  const fileEntryKey = fileEntryKeyFacet.serialize(fileEntry);

  return async (dispatch: IItemCacheDispatch) => {
    const sourceDirectoryPage = dispatch(readItems((itemCache: IItemCache) => itemCache.demandItemFacet(directoryPageFacet, itemKey)));

    if (sourceDirectoryPage && sourceDirectoryPage.getEntries) {
      const directoryPage = await sourceDirectoryPage.getEntries();

      const itemResolver = new ItemResolver();

      const {
        entries,
        getEntries
      } = directoryPage;

      const childItemKeys: string[] = [];

      for (const childEntry of entries) {
        const childEntryKey = fileEntryKeyFacet.serialize({
          ...fileEntry,
          path: `${fileEntry.path}/${childEntry.name}`
        });

        itemResolver.resolveItems({
          items: {
            [childEntryKey]: {
              ...entryFacet.pack(childEntry),
              ...parentFolderItemFacet.pack({
                itemKey: fileEntryKey
              })
            }
          }
        });

        childItemKeys.push(childEntryKey);
      }

      if (getEntries) {
        const childDirectoryPageKey = fileEntryDirectoryPageKeyFacet.serialize({
          fileEntryChildren: fileEntryChildren,
          startIndex: startIndex + entries.length
        });

        itemResolver.merge(resolveNewPage({
          itemKey: childDirectoryPageKey
        }).itemResolver);

        itemResolver.resolveItems({
          items: {
            [childDirectoryPageKey]: {
              ...directoryPageFacet.pack(directoryPage)
            }
          }
        });

        childItemKeys.push(childDirectoryPageKey);
      }

      itemResolver.merge(resolveExistingPage({
        itemKey: itemKey,
        itemSetKey: fileEntryChildrenKeyFacet.serialize(fileEntryChildren),
        childItemKeys: childItemKeys
      }).itemResolver);

      dispatch(itemResolver.execute());
    }
  };
}

function deleteEntryBatchItem(
  payload: ISingleOperationPayload<typeof entryBatchKeyFacet.type, typeof deleteItemsOperation.type>
): IOperationHandlerThunkAction {
  const {
    itemKey
  } = payload;

  return async (dispatch: IItemCacheDispatch) => {

    dispatch(clearItems({
      items: dispatch(readItems((itemCache: IItemCache) => {
        const itemsToClear: (typeof clearItems.payloadType)['items'] = {};

        const itemKeyQueue: string[] = [itemKey];

        while (itemKeyQueue.length > 0) {
          const currentItemKey = itemKeyQueue.pop()!;

          itemsToClear[currentItemKey] = true;

          const defaultChildItemSet = itemCache.demandItemFacet(defaultChildItemSetFacet, currentItemKey);

          const itemSetKey = defaultChildItemSet && defaultChildItemSet.itemKey;

          if (itemSetKey) {
            itemsToClear[itemSetKey] = true;
          }

          const childItems = itemCache.demandItemFacet(childItemsFacet, itemSetKey);

          const childItemKeys = childItems && childItems.itemKeys;

          if (childItemKeys) {
            itemKeyQueue.push(...childItemKeys);
          }
        }

        return itemsToClear;
      }))
    }));
  };
}

function getFileName(name: string): {
  name: string;
  extension: string;
} {
  const indexOfDot = name.lastIndexOf('.');

  if (indexOfDot > -1) {
    return {
      name: name.slice(0, indexOfDot),
      extension: name.slice(indexOfDot)
    };
  }

  return {
    name: name,
    extension: ''
  };
}
